import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import HintButton from "@/components/HintButton";
import BiFiltersSidebar from "@/components/bi/BiFiltersSidebar";
import BiKpiGrid from "@/components/bi/BiKpiGrid";
import BiOverviewTab from "@/components/bi/BiOverviewTab";
import BiStoresTab from "@/components/bi/BiStoresTab";
import BiProductsTab from "@/components/bi/BiProductsTab";
import BiColorsTab from "@/components/bi/BiColorsTab";
import BiAbcTab from "@/components/bi/BiAbcTab";
import BiSupplyTab from "@/components/bi/BiSupplyTab";
import BiInsights from "@/components/bi/BiInsights";
import { buildBiParams } from "@/components/bi/biShared";
import { downloadBlob } from "@/components/production/pcShared";
import { ChartLineUp, Storefront, Barcode, Palette, FunnelSimple, ListNumbers, FileXls, FilePdf, Factory } from "@phosphor-icons/react";

const todayLocal = () => new Date().toLocaleDateString("en-CA");
const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toLocaleDateString("en-CA");
};

const EMPTY_FILTERS = () => ({
  date_from: daysAgo(29), date_to: todayLocal(), compare: "prev",
  stores: [], types: [], colors: [], capacities: [], skus: [],
  qty_min: "", qty_max: "",
});

const TABS = [
  { key: "overview", label: "Visão Geral", icon: ChartLineUp },
  { key: "stores", label: "Lojas & Canais", icon: Storefront },
  { key: "products", label: "Produtos & SKUs", icon: Barcode },
  { key: "abc", label: "Curva ABC", icon: ListNumbers },
  { key: "colors", label: "Cores", icon: Palette },
  { key: "supply", label: "Estoque & Produção", icon: Factory },
];

export default function SalesDashboard() {
  const [options, setOptions] = useState(null);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [preset, setPreset] = useState("30d");
  const [showFilters, setShowFilters] = useState(true);
  const [tab, setTab] = useState("overview");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(null);

  const doExport = async (format) => {
    setExporting(format);
    try {
      await downloadBlob("/bi/export", { ...buildBiParams(filters), format },
        `painel_vendas_${filters.date_from}_${filters.date_to}.${format}`);
      toast.success(`Exportação ${format.toUpperCase()} concluída`);
    } catch {
      toast.error("Falha na exportação");
    } finally {
      setExporting(null);
    }
  };

  useEffect(() => {
    api.get("/bi/filter-options")
      .then(({ data: d }) => setOptions(d))
      .catch(() => toast.error("Falha ao carregar opções de filtro"));
  }, []);

  useEffect(() => {
    if (!filters.date_from || !filters.date_to || filters.date_from > filters.date_to) return undefined;
    let alive = true;
    setLoading(true);
    const t = setTimeout(() => {
      api.get("/bi/dashboard", { params: buildBiParams(filters) })
        .then(({ data: d }) => alive && setData(d))
        .catch(() => alive && toast.error("Falha ao carregar o painel de vendas"))
        .finally(() => alive && setLoading(false));
    }, 350);
    return () => { alive = false; clearTimeout(t); };
  }, [filters]);

  const onClear = () => {
    // Limpa só as dimensões — preserva o período e o modo de comparação escolhidos
    setFilters((f) => ({ ...EMPTY_FILTERS(), date_from: f.date_from, date_to: f.date_to, compare: f.compare }));
  };

  const activeFilterCount = ["stores", "types", "colors", "capacities", "skus"]
    .reduce((a, k) => a + (filters[k]?.length || 0), 0) +
    (filters.qty_min ? 1 : 0) + (filters.qty_max ? 1 : 0);

  return (
    <div className="p-3 sm:p-4 space-y-3" data-testid="sales-dashboard-page">
      <div className="flex flex-wrap items-center gap-2">
        <div>
          <div className="overline text-[10px] text-zinc-500">RELATÓRIOS · BI COMERCIAL</div>
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight font-head flex items-center gap-2">
            Painel de Vendas
            <HintButton text="Visão executiva do faturamento: bruto, líquido, pedidos, unidades, ticket médio, evolução temporal, comparação com o período anterior (ou ano anterior), lojas, SKUs, tipos, capacidades e cores. Todos os números respeitam os filtros do painel lateral e são calculados sobre os pedidos reais importados do Bling." />
          </h1>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-1.5">
          <button className="btn btn-tight" onClick={() => doExport("xlsx")} disabled={!!exporting || !data} data-testid="bi-export-xlsx">
            <FileXls size={14} className="inline mr-1" />{exporting === "xlsx" ? "..." : "Excel"}
          </button>
          <button className="btn btn-tight" onClick={() => doExport("pdf")} disabled={!!exporting || !data} data-testid="bi-export-pdf">
            <FilePdf size={14} className="inline mr-1" />{exporting === "pdf" ? "..." : "PDF"}
          </button>
          <button className={`btn btn-tight ${showFilters ? "!bg-black !text-white" : ""}`}
            onClick={() => setShowFilters(!showFilters)} data-testid="bi-toggle-filters">
            <FunnelSimple size={14} className="inline mr-1" />
            Filtros{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
          </button>
        </div>
      </div>

      <BiKpiGrid data={data} loading={loading} />

      <div className="flex flex-col lg:flex-row gap-3 items-start">
        {showFilters && (
          <BiFiltersSidebar
            options={options} filters={filters} setFilters={setFilters}
            preset={preset} setPreset={setPreset}
            onClear={onClear} onClose={() => setShowFilters(false)}
          />
        )}
        <div className={`flex-1 min-w-0 w-full space-y-3 ${loading ? "opacity-60 pointer-events-none" : ""}`}>
          <div className="flex gap-1 overflow-x-auto pb-1" data-testid="bi-tabs">
            {TABS.map((t) => {
              const Icon = t.icon;
              return (
                <button key={t.key} onClick={() => setTab(t.key)}
                  className={`flex items-center gap-1.5 text-xs px-3 py-1.5 border rounded whitespace-nowrap transition-colors ${tab === t.key ? "bg-black text-white border-black" : "border-zinc-300 hover:border-black"}`}
                  data-testid={`bi-tab-${t.key}`}>
                  <Icon size={14} weight={tab === t.key ? "fill" : "regular"} />
                  {t.label}
                </button>
              );
            })}
          </div>
          {!data && loading && (
            <div className="tile p-8 text-center text-zinc-400 animate-pulse" data-testid="bi-loading">Carregando painel de vendas...</div>
          )}
          {data && tab === "overview" && (
            <>
              <BiInsights insights={data.insights} />
              <BiOverviewTab data={data} />
            </>
          )}
          {data && tab === "stores" && <BiStoresTab data={data} />}
          {data && tab === "products" && <BiProductsTab data={data} />}
          {data && tab === "abc" && <BiAbcTab data={data} />}
          {data && tab === "colors" && <BiColorsTab data={data} />}
          {tab === "supply" && <BiSupplyTab filters={filters} />}
        </div>
      </div>
    </div>
  );
}
